import { Component, signal, computed, inject, effect } from "@angular/core";
import { Router } from "@angular/router";
import {
  FilterSet,
  FilterSetType,
} from "src/app/core/interfaces/filter-interface";
import { MobilitySearchStore } from "../../store/mobility-search-store";
import { FilterSection } from "src/app/shared/components/filter-section/filter-section";
import { Button } from "src/app/shared/components/utils/button/button";
import { FiltersService } from "../../../core/services/filters/filters-service";
import { Loader } from "src/app/shared/components/loader/loader";
import { TranslocoModule } from "@jsverse/transloco";
import {
  CreateFavoriteFilterSearchRequestInterface,
  SearchType,
} from "src/app/core/interfaces/favorite-interface";
import { FavoritesService } from "../../services/favorites/favorites-service";
import { InputModal } from "src/app/shared/components/utils/input-modal/input-modal";
import { LanguageService } from "src/app/core/services/language-service";
import { FavoriteStore } from "src/app/features/store/favorite-store";
import { FavoriteSearchErrorModal } from "src/app/shared/components/favorite-search-error-modal/favorite-search-error-modal";
import { ErrorModalService } from "src/app/core/services/error-modal-service";

@Component({
  selector: "app-mobility-ref-search-page",
  imports: [
    FilterSection,
    Button,
    Loader,
    TranslocoModule,
    InputModal,
    FavoriteSearchErrorModal,
  ],
  templateUrl: "./mobility-ref-search-page.html",
})
export class MobilityRefSearchPage {
  router = inject(Router);
  filtersService = inject(FiltersService);
  favoritesService = inject(FavoritesService);
  favoriteStore = inject(FavoriteStore);
  mobilitySearchStore = inject(MobilitySearchStore);
  private readonly languageService = inject(LanguageService);
  private readonly errorModalService = inject(ErrorModalService);

  activitiesEmpty = signal<boolean>(true);
  localisationsEmpty = signal<boolean>(true);
  filters = signal<FilterSet[]>([]);
  loading = signal<boolean>(false);
  activitiesFiltersSelected = signal<FilterSet>(null);
  locationFiltersSelected = signal<FilterSet>(null);
  isAddToFavoriteModalOpen = signal(false);
  isFavoriteSearchSuccessSaved = signal<boolean>(false);

  activitiesFilters = computed(() =>
    this.filters().find((f) => f.filterSetType === FilterSetType.Activity)
  );
  localisationFilters = computed(() =>
    this.filters().find(
      (f) => f.filterSetType === FilterSetType.GeographicalLocation
    )
  );

  constructor() {
    effect(() => {
      this.languageService.currentLanguage();
      this.loadFilters();
    });
  }

  async loadFilters(): Promise<void> {
    this.loading.set(true);
    if (this.mobilitySearchStore.activitiesFiltersSelected()) {
      this.activitiesFiltersSelected.set(
        this.mobilitySearchStore.activitiesFiltersSelected()
      );
    }
    if (this.mobilitySearchStore.locationFiltersSelected()) {
      this.locationFiltersSelected.set(
        this.mobilitySearchStore.locationFiltersSelected()
      );
    }
    try {
      await this.mobilitySearchStore.loadMobilityRefFilters();
      const filters: FilterSet[] = [
        this.mobilitySearchStore.activitiesFilters(),
        this.mobilitySearchStore.locationFilters(),
      ];

      const filterSetGeographicalLocation = filters.find(
        (fs) => fs.filterSetType == FilterSetType.GeographicalLocation
      );
      const filterSetActivity = filters.find(
        (fs) => fs.filterSetType == FilterSetType.Activity
      );

      filterSetActivity.filtersCategories =
        this.filtersService.getAllFilterCategories(filterSetActivity.filters);
      filterSetGeographicalLocation.filtersCategories =
        this.filtersService.getAllFilterCategories(
          filterSetGeographicalLocation.filters
        );
      filterSetActivity.filters = filterSetActivity.filters.filter(
        (x) => x.filters === null
      );
      filterSetGeographicalLocation.filters =
        filterSetGeographicalLocation.filters.filter((x) => x.filters === null);

      this.mobilitySearchStore.updateSelectedFiltersTranslations();

      if (this.mobilitySearchStore.activitiesFiltersSelected()) {
        this.activitiesFiltersSelected.set(
          this.mobilitySearchStore.activitiesFiltersSelected()
        );
      }
      if (this.mobilitySearchStore.locationFiltersSelected()) {
        this.locationFiltersSelected.set(
          this.mobilitySearchStore.locationFiltersSelected()
        );
      }

      this.filters.set(filters);
    } catch (error) {
      if (this.errorModalService.shouldOpenErrorModal(error)) {
        this.errorModalService.openErrorModal();
      }
      this.filters.set([]);
    } finally {
      this.loading.set(false);
    }
  }

  isFiltersEmpty = computed(
    () => this.activitiesEmpty() || this.localisationsEmpty()
  );

  onActivitiesEmpty(isEmpty: boolean) {
    this.activitiesEmpty.set(isEmpty);
  }

  onLocalisationsEmpty(isEmpty: boolean) {
    this.localisationsEmpty.set(isEmpty);
  }

  startResearch() {
    this.mobilitySearchStore.setFiltersSelected(
      this.locationFiltersSelected()!,
      this.activitiesFiltersSelected()!
    );
    this.router.navigate(["/mobility/referents-results"]);
  }

  updateFiltersSelected(filterUpdated: FilterSet) {
    this.isFavoriteSearchSuccessSaved.set(false);
    switch (filterUpdated.filterSetType) {
      case FilterSetType.Activity:
        this.activitiesFiltersSelected.set(filterUpdated);
        break;
      case FilterSetType.GeographicalLocation:
        this.locationFiltersSelected.set(filterUpdated);
        break;
    }
  }

  async saveSearch(searchName: string): Promise<void> {
    const createFavoriteFilterSearchRequest: CreateFavoriteFilterSearchRequestInterface =
      {
        name: searchName,
        searchType: SearchType.MobilityReferentSearch,
        research: {
          selectedFilters: [
            this.locationFiltersSelected(),
            this.activitiesFiltersSelected(),
          ],
        },
      };

    try {
      await this.favoritesService.saveSearch(
        createFavoriteFilterSearchRequest
      );
    } catch {
      this.favoriteStore.setFavoriteSearchesErrorModalOpen(true);
    } finally {
      this.isFavoriteSearchSuccessSaved.set(true);
      this.isAddToFavoriteModalOpen.set(false);
    }
  }

  openAddFavoriteModal(): void {
    this.isAddToFavoriteModalOpen.set(true);
  }

  closeAddFavoriteModal(): void {
    this.isAddToFavoriteModalOpen.set(false);
  }
}
